import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_API_KEY } from '@src/utils/constants';
import { LoaderComponent } from '@component/LoaderComponent/LoaderComponent';
import { logger } from '@utils/logger';

// Module-level constant: a new array on every render makes the loader warn and reload.
const LIBRARIES: Libraries = ['places', 'maps', 'geocoding', 'geometry'];
// How long the app waits for Google Maps before opening without it.
const LOAD_TIMEOUT_MS = 15000;

type MapsStatus = 'loading' | 'ready' | 'failed';

const GoogleMapsContext = createContext<MapsStatus>('loading');

/** 'ready' when Google Maps can be used; map components render a fallback otherwise. */
export const useGoogleMapsStatus = () => useContext(GoogleMapsContext);

/**
 * Loads the Google Maps JS API once for the whole app. While it loads the app waits (as before), but a
 * failed or stuck load (blocked network, QUIC/firewall errors on maps.googleapis.com) no longer keeps the
 * portal on a spinner: after LOAD_TIMEOUT_MS or a load error the app opens and only maps show "couldn't
 * load". If Maps arrives later anyway, maps switch on.
 */
export const GoogleMapsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: LIBRARIES,
  });
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (isLoaded) return;
    const timer = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded]);

  useEffect(() => {
    if (loadError) logger.error('Google Maps failed to load:', loadError);
    else if (timedOut && !isLoaded)
      logger.error('Google Maps did not load in time; opening without maps');
  }, [loadError, timedOut, isLoaded]);

  const status: MapsStatus = isLoaded
    ? 'ready'
    : loadError || timedOut
      ? 'failed'
      : 'loading';

  if (status === 'loading') {
    return <LoaderComponent />;
  }
  return (
    <GoogleMapsContext.Provider value={status}>
      {children}
    </GoogleMapsContext.Provider>
  );
};
